import Decimal from 'decimal.js';
import { utils } from '~/utils';
import { preapprovalPeriodWordMap, recurrencePeriodWordMap } from '~/utils/billing-period-text';
import pluralize from '~/utils/pluralize';
import { DiscountModel, DiscountType } from '../DiscountModel';
import { RoundPrices } from '../PriceIncreaseModel';
import * as serviceBillingModel from '../service-billing/selectors';
import { ServiceCostType } from './model';
export function isFixedPrice(cost) {
    return cost.pricingType === ServiceCostType.Fixed;
}
export function isVariablePrice(cost) {
    return cost.pricingType === ServiceCostType.Variable;
}
export function isRangePrice(cost) {
    return cost.pricingType === ServiceCostType.Range;
}
export function price(cost) {
    if (cost.pricingType === ServiceCostType.Fixed) {
        return cost.price;
    } else if (cost.pricingType === ServiceCostType.Variable) {
        return cost.price;
    } else {
        return cost.maxPrice;
    }
}
export function minPrice(cost) {
    return cost.pricingType === ServiceCostType.Range ? cost.minPrice : null;
}
export function finalPrice(cost, quantity) {
    if (isRangePrice(cost)) {
        return utils.applyDiscount(cost.maxPrice, cost.discount);
    } else if (isFixedPrice(cost)) {
        return utils.applyDiscount(cost.price, cost.discount);
    } else {
        return utils.calculateFinalPrice(cost.price, cost.discount, quantity);
    }
}
export function applyDiscount(cost) {
    if (isFixedPrice(cost) || isVariablePrice(cost)) {
        return utils.applyDiscount(cost.price, cost.discount);
    } else {
        return utils.applyDiscount(cost.maxPrice, cost.discount);
    }
}
export function minPriceFinal(cost) {
    return cost.pricingType === ServiceCostType.Range && cost.minPrice ? utils.applyDiscount(cost.minPrice, cost.discount) : null;
}
export function discount(cost) {
    return cost.discount;
}
export function expiredDiscount(cost) {
    return cost.expiredDiscount;
}
export function discountPeriodExpiration(cost) {
    return cost.discount.expiration();
}
export function unitCap(cost) {
    return isVariablePrice(cost) ? cost.unitCap : null;
}
export function preapprovedControlledByAdvancedSettings(cost) {
    return isVariablePrice(cost) && (cost.preapprovedControlledByAdvancedSettings || false);
}
export function unitName(cost) {
    if (cost.pricingType === ServiceCostType.Fixed || cost.pricingType === ServiceCostType.Range) {
        return null;
    } else {
        return cost.unitName;
    }
}
export function priceIncrease(cost) {
    return cost.priceIncrease;
}
export function nextYearPrice(cost) {
    var _cost_discount_expiration, _cost_discount;
    if (cost.priceIncrease.isNonPositiveIncrease()) {
        return null;
    }
    const basePrice = ((_cost_discount = cost.discount) === null || _cost_discount === void 0 ? void 0 : (_cost_discount_expiration = _cost_discount.expiration()) === null || _cost_discount_expiration === void 0 ? void 0 : _cost_discount_expiration.hasExpiration()) ? price(cost) : finalPrice(cost, 1);
    const rawResult = basePrice.times(cost.priceIncrease.percentIncrease().plus(100)).div(100);
    switch(cost.priceIncrease.roundPrices()){
        case RoundPrices.None:
            return rawResult.toDP(2).toNumber();
        case RoundPrices.Down1:
            return rawResult.floor().toNumber();
        case RoundPrices.Down5:
            return rawResult.minus(rawResult.mod(5)).toNumber();
        case RoundPrices.Down10:
            {
                return rawResult.minus(rawResult.mod(10)).toNumber();
            }
    }
}
export const defaultUnitName = 'hour';
export const defaultUnitNameShort = 'hr';
export function formattedUnitName(cost) {
    const un = unitName(cost);
    return un || defaultUnitName;
}
export function formattedUnitNameShort(cost) {
    const un = unitName(cost);
    return un || defaultUnitNameShort;
}
function preapprovedPeriodSuffix(billing) {
    if (!billing) return '';
    if (serviceBillingModel.isOnDemand(billing)) {
        const word = preapprovalPeriodWordMap[billing.preapprovalPeriod];
        return word ? " per ".concat(word) : '';
    }
    if (serviceBillingModel.isRecurring(billing)) {
        const word = recurrencePeriodWordMap[billing.recurrencePeriod];
        return word ? " per ".concat(word) : '';
    }
    return '';
}
export function stringifyServiceCost(cost, billing) {
    var _ref, _ref1;
    const applyDiscount = function applyDiscount(price) {
        let discount = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : new Decimal(0), discountType = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : DiscountType.Value;
        const discountedPrice = utils.applyDiscount(price, DiscountModel.make(discountType, discount));
        return utils.formatDollarAmount(discountedPrice);
    };
    const { discount, pricingType } = cost;
    const discountAmount = (_ref = discount === null || discount === void 0 ? void 0 : discount.amount()) !== null && _ref !== void 0 ? _ref : new Decimal(0);
    const discountType = (_ref1 = discount === null || discount === void 0 ? void 0 : discount.type()) !== null && _ref1 !== void 0 ? _ref1 : DiscountType.Value;
    switch(pricingType){
        case ServiceCostType.Fixed:
            return {
                costDisplay: applyDiscount(cost.price, discountAmount, discountType)
            };
        case ServiceCostType.Variable:
            {
                const unitCap = cost.unitCap;
                const unitName = formattedUnitName(cost);
                const unitCapDisplay = unitCap ? "".concat(unitCap, " ").concat(pluralize(unitName, unitCap.toNumber()), " preapproved").concat(preapprovedPeriodSuffix(billing)) : undefined;
                return {
                    costDisplay: "".concat(applyDiscount(cost.price, discountAmount, discountType), "/").concat(unitName),
                    unitCapDisplay
                };
            }
        case ServiceCostType.Range:
            {
                if (cost.minPrice) {
                    return {
                        costDisplay: "".concat(applyDiscount(cost.minPrice, discountAmount, discountType), "—").concat(applyDiscount(cost.maxPrice, discountAmount, discountType))
                    };
                } else {
                    return {
                        costDisplay: "Up to ".concat(applyDiscount(cost.maxPrice, discountAmount, discountType))
                    };
                }
            }
    }
}
export function formattedCost(cost) {
    const { costDisplay, unitCapDisplay } = stringifyServiceCost(cost);
    return unitCapDisplay ? "".concat(costDisplay, " • ").concat(unitCapDisplay) : "".concat(costDisplay);
}
export function match(cost, visitor) {
    switch(cost.pricingType){
        case ServiceCostType.Fixed:
            return visitor.fixed(cost);
        case ServiceCostType.Variable:
            return visitor.variable(cost);
        case ServiceCostType.Range:
            return visitor.range(cost);
    }
}
export function isDiscountAndRateEqual(cost, costToCompare) {
    var _minPrice, _ref;
    var _minPrice1, _costToCompare_discount, _discount, _discount1, _costToCompare_discount1;
    const isEqual = cost.pricingType === costToCompare.pricingType && price(cost).equals(price(costToCompare)) && (isRangePrice(cost) ? (_minPrice1 = minPrice(cost)) === null || _minPrice1 === void 0 ? void 0 : _minPrice1.equals((_minPrice = minPrice(costToCompare)) !== null && _minPrice !== void 0 ? _minPrice : new Decimal(0)) : true) && ((_discount = discount(cost)) === null || _discount === void 0 ? void 0 : _discount.amount().equals((_ref = (_costToCompare_discount = costToCompare.discount) === null || _costToCompare_discount === void 0 ? void 0 : _costToCompare_discount.amount()) !== null && _ref !== void 0 ? _ref : new Decimal(0))) && ((_discount1 = discount(cost)) === null || _discount1 === void 0 ? void 0 : _discount1.type()) === ((_costToCompare_discount1 = costToCompare.discount) === null || _costToCompare_discount1 === void 0 ? void 0 : _costToCompare_discount1.type());
    return isEqual || false;
}
